// React imports
import { FunctionComponent, useState } from 'react';

// Third-party imports
import { useMediaQuery } from 'react-responsive';

// Types
import { Promocode } from '@spike/promocode-model';
import { PaginationProps } from 'components/UI/V3/Pagination/Pagination';

// Hooks
import { usePromoCodes } from '../PromoCodesProvider';

// UI Components
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';

// Local Components
import PromoCodesRow from './PromoCodesRow';
import PromoCodesIsEmpty from './PromoCodesIsEmpty';

// Styles
import styles from './PromoCodesTable.module.scss';

interface PromoCodesTableProps {
    onEdit?: (promoCode: Promocode) => void;
    onDeactivate?: (promoCode: Promocode) => void;
    onActivate?: (promoCode: Promocode) => void;
    onUsage?: (promoCode: Promocode) => void;
    className?: string;
}

export const PromoCodesTable: FunctionComponent<PromoCodesTableProps> = ({
    onEdit,
    onDeactivate,
    onActivate,
    onUsage,
    className
}) => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

    const {
        selectedFilter,
        searchText,
        isLoading,
        promoCodes,

        page,
        totalPages,
        size,
        setPage,
        setSize
    } = usePromoCodes();

    const pagination: PaginationProps = {
        page,
        totalPages,
        size,
        isLoading
    };

    const isEmpty = promoCodes.length === 0;

    const renderTableContent = () => {
        if (isEmpty && (searchText || selectedFilter !== 'all') && !isLoading) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={6}
                            style={{ textAlign: 'center', padding: '0' }}
                        >
                            <PromoCodesIsEmpty
                                title="No promo codes found"
                                text="No Promo Codes match your filter selection. Try changing or clearing the filter to see more."
                            />
                        </td>
                    </tr>
                </TableBody>
            );
        }
        // Empty state only
        if (isEmpty && !isLoading) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={6}
                            style={{ textAlign: 'center', padding: '0' }}
                        >
                            <PromoCodesIsEmpty
                                title="No promo codes yet"
                                text="Add your first promo code to start offering discounts."
                            />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        // Always show existing data - loading spinner only in pagination
        return (
            <TableBody>
                {promoCodes.map((promoCode, index) => (
                    <PromoCodesRow
                        key={promoCode.id}
                        promoCode={promoCode}
                        isLast={promoCodes.length === index + 1}
                        isMobile={isMobile}
                        onEdit={onEdit}
                        onDeactivate={onDeactivate}
                        onActivate={onActivate}
                        onUsage={onUsage}
                        isDropdownOpen={openDropdownId === String(promoCode.id)}
                        onDropdownToggle={isOpen => setOpenDropdownId(isOpen ? String(promoCode.id) : null)}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            pagination={pagination}
            onPageChange={setPage}
            onSizeChange={setSize}
            className={`${className} ${styles.table}`}
            fullHeight={true}
            firstColumnFixed={isMobile}
        >
            <TableHeader className={styles.tableHeader}>
                <TableHeaderCell>Promo</TableHeaderCell>
                <TableHeaderCell>Start Date</TableHeaderCell>
                <TableHeaderCell>End Date</TableHeaderCell>
                <TableHeaderCell>Used</TableHeaderCell>
                <TableHeaderCell>Total Sales</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
            </TableHeader>

            {renderTableContent()}
        </Table>
    );
};

export default PromoCodesTable;
